import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Country, GeneralErrorCause, LoadAllCountriesUseCase } from '51trips-frontend-core'
import { matchSorter } from 'match-sorter'
import moment from 'moment'

import { ApiKeyTripbookRepository } from '../../repositories/ApiKeyTripbookRepository.ts'
import { AutocompleteSearch } from '../AutocompleteSearch.tsx'
import { getFlagUrl } from '../../utils/flagUrl.ts'
import { Input } from '../Input.tsx'
import { CountryRepository } from '../../repositories/CountryRepository.ts'
import { OptionWithData } from '../../types'
import crossIcon from '../../assets/icons/crossIcon.png'
import { getAgencyAssetUrl, getAssetUrl } from '../../utils/AssetsCDN.ts'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { storage } from '../../firebase.ts'

const loadAllCountriesUseCase = new LoadAllCountriesUseCase(new CountryRepository())

const CheckIcon = () => (
    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
    </svg>
)

const SectionCard = ({ title, children, isCompleted }: { title: string; children: React.ReactNode; isCompleted?: boolean }) => (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-6 relative">
        <div className="flex justify-between items-start mb-6">
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center border transition-all duration-300 ${isCompleted ? 'bg-black' : 'bg-white border-neutralBlack-300'}`}>
                {isCompleted && <CheckIcon />}
            </div>
        </div>
        {children}
    </div>
)

interface TripBookingFormProps {
    apiKey: string
}

export const TripBookingForm: React.FC<TripBookingFormProps> = ({ apiKey }) => {
    const { t, i18n } = useTranslation()

    const repository = useMemo(() => new ApiKeyTripbookRepository(apiKey), [apiKey])

    const [agency, setAgency] = useState<any>(null)
    const [isLoadingAgency, setIsLoadingAgency] = useState(true)
    const [agencyError, setAgencyError] = useState<'unauthorized' | 'unknown' | null>(null)

    useEffect(() => {
        repository.getAgency()
            .then(setAgency)
            .catch((err) => {
                if (err?.cause === GeneralErrorCause.unauthorized) {
                    setAgencyError('unauthorized')
                } else {
                    setAgencyError('unknown')
                }
            })
            .finally(() => setIsLoadingAgency(false))
    }, [repository])

    const countries: Country[] = useMemo(() => loadAllCountriesUseCase.loadAll(), [i18n.language])

    const countryOptions: OptionWithData<Country>[] = useMemo(() =>
        countries.map((c: any) => ({ label: c.title || c.name, value: c.id, data: c })),
        [countries]
    )

    // ── Traveler ────────────────────────────────────────────────────────────
    const [travelerFirstName, setTravelerFirstName] = useState('')
    const [travelerLastName, setTravelerLastName] = useState('')
    const [travelerEmail, setTravelerEmail] = useState('')

    // ── Trip data ───────────────────────────────────────────────────────────
    const [tripStartDate, setTripStartDate] = useState('')
    const [tripEndDate, setTripEndDate] = useState('')
    const [circuitName, setCircuitName] = useState('')
    const [circuitCountries, setCircuitCountries] = useState<string[]>([])
    const [countrySearchQuery, setCountrySearchQuery] = useState('')
    const [dateError, setDateError] = useState<string | undefined>(undefined)

    // ── Order ───────────────────────────────────────────────────────────────
    const [tripReference, setTripReference] = useState('')
    const [tripObservations, setTripObservations] = useState('')

    // ── PDF Itinerary ────────────────────────────────────────────────────────
    const [itineraryPdfFileUrl, setItineraryPdfFileUrl] = useState('')
    const [pdfFileName, setPdfFileName] = useState('')
    const [isUploadingPdf, setIsUploadingPdf] = useState(false)
    const pdfInputRef = useRef<HTMLInputElement>(null)

    // ── UI state ────────────────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false)
    const [submitError, setSubmitError] = useState<string | undefined>(undefined)

    const filteredCountryOptions = useMemo(() => {
        if (!countrySearchQuery) return []
        return matchSorter(countryOptions, countrySearchQuery, { keys: ['label'] })
    }, [countryOptions, countrySearchQuery])

    const handleDateChange = (start: string, end: string) => {
        if (start && end && start > end) {
            setDateError(t('invalidStartDate', { ns: 'dashboard' }))
        } else {
            setDateError(undefined)
        }
    }

    const handleSelectCountry = (option: OptionWithData<Country>) => {
        if (!circuitCountries.includes(option.value)) {
            setCircuitCountries((prev) => [...prev, option.value])
        }
        setCountrySearchQuery('')
    }

    const removeCountry = (code: string) => {
        setCircuitCountries((prev) => prev.filter((c) => c !== code))
    }

    const isTravelerComplete = !!(travelerFirstName || travelerLastName || travelerEmail)
    const isTripComplete = !!(tripStartDate && tripEndDate && !dateError)
    const isOrderComplete = !!tripReference

    const isFormEnabled = !isLoading && !isUploadingPdf && isTripComplete && isOrderComplete

    const cleanForm = () => {
        setTravelerFirstName('')
        setTravelerLastName('')
        setTravelerEmail('')
        setTripStartDate('')
        setTripEndDate('')
        setCircuitName('')
        setCircuitCountries([])
        setCountrySearchQuery('')
        setTripReference('')
        setTripObservations('')
        setDateError(undefined)
        setSubmitError(undefined)
        setItineraryPdfFileUrl('')
        setPdfFileName('')
        setIsUploadingPdf(false)
        if (pdfInputRef.current) pdfInputRef.current.value = ''
    }

    const handlePdfFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setIsUploadingPdf(true)
        setPdfFileName(file.name)
        const path = `tripItineraries/${Date.now()}_${file.name}`
        const fileRef = storageRef(storage, path)
        const uploadTask = uploadBytesResumable(fileRef, file)
        uploadTask.on('state_changed', null,
            (error) => { console.error(error); setIsUploadingPdf(false) },
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref)
                setItineraryPdfFileUrl(url)
                setIsUploadingPdf(false)
            }
        )
    }

    const buildItinerary = () => {
        if (circuitName || circuitCountries.length > 0) {
            return {
                name: circuitName || t('itineraryTitlePlaceholder', { ns: 'dashboard' }),
                countryCodes: circuitCountries.length > 0 ? circuitCountries : undefined,
                personalized: true,
                startDate: tripStartDate ? moment.utc(tripStartDate, 'YYYY-MM-DD').valueOf() : undefined,
                endDate: tripEndDate ? moment.utc(tripEndDate, 'YYYY-MM-DD').valueOf() : undefined,
            }
        }
        return undefined
    }

    const submit = async () => {
        setIsLoading(true)
        setSubmitError(undefined)

        try {
            const payload: any = {
                agencyID: agency?.id || undefined,
                partnerOrderID: tripReference || undefined,
                observations: tripObservations?.length > 0 ? tripObservations : undefined,
                itinerary: buildItinerary(),
                traveler: (travelerFirstName || travelerLastName || travelerEmail) ? {
                    firstName: travelerFirstName || undefined,
                    lastName: travelerLastName || undefined,
                    email: travelerEmail || undefined,
                } : undefined,
                itineraryPdfFileUrl: itineraryPdfFileUrl || undefined,
            }

            await repository.createTripbook(payload)
            cleanForm()
            setShowSuccess(true)
        } catch (err) {
            console.error(err)
            setSubmitError(t('tripBooking.submitError', { ns: 'dashboard' }))
        } finally {
            setIsLoading(false)
        }
    }

    if (isLoadingAgency) {
        return (
            <div className="min-h-screen flex items-center justify-center"
                style={{ background: 'url(https://cdn.51trips.com/images/pexels-eva-purrer-1937326549-28830598.webp) center center / cover' }}>
                <div style={{ background: 'rgba(0,0,0,0.25)', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
                <IconSpinner className="size-16 text-white relative z-10" />
            </div>
        )
    }

    if (agencyError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="bg-white rounded-2xl shadow-xl p-10 max-w-sm w-full mx-4 flex flex-col items-center text-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                        <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">
                        {agencyError === 'unauthorized'
                            ? t('tripBooking.errorUnauthorizedTitle', { ns: 'dashboard' })
                            : t('tripBooking.errorGenericTitle', { ns: 'dashboard' })}
                    </h2>
                    <p className="text-sm text-gray-500">
                        {agencyError === 'unauthorized'
                            ? t('tripBooking.errorUnauthorizedMessage', { ns: 'dashboard' })
                            : t('tripBooking.errorGenericMessage', { ns: 'dashboard' })}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen p-4 lg:p-8 font-sans text-gray-800"
            style={{ background: 'url(https://cdn.51trips.com/images/pexels-eva-purrer-1937326549-28830598.webp) center center / cover' }}>
            <div id="overlay" style={{ background: 'rgba(0,0,0,0.25)', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />

            {/* Header */}
            <header className="relative z-10 flex justify-center lg:justify-start items-center gap-4 mb-8 lg:mb-12 lg:ml-10">
                <img
                    className="invert brightness-0 h-[20px] object-contain"
                    src={getAssetUrl('Logo_51T_RGB.png')}
                    alt="51trips"
                />
                {agency && (
                    <>
                        <img className="size-[13px] invert brightness-0" src={crossIcon} alt="x" />
                        {agency.logoName
                            ? <img className="invert brightness-0 max-w-[100px] h-[60px] object-contain" src={getAgencyAssetUrl(agency.logoName)} alt={agency.name} />
                            : <span className="text-white font-medium">{agency.name}</span>
                        }
                    </>
                )}
            </header>

            <div className="relative z-10 max-w-4xl mx-auto">
                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                        {t('tripBooking.pageTitle', { ns: 'dashboard' })}
                    </h1>
                    <p className="text-white/80 text-sm lg:text-base">
                        {t('tripBooking.pageSubtitle', { ns: 'dashboard' })}
                    </p>
                </div>

                <div className="flex flex-col gap-6">

                    {/* Section 1: Traveler */}
                    <SectionCard title={t('mainTraveler', { ns: 'dashboard' })} isCompleted={isTravelerComplete}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500">{t('firstName', { ns: 'dashboard' })}</label>
                                <Input id="firstName" type="text" invalid={false} placeholder={t('dialogs.placeholderFirstName', { ns: 'dashboard' })}
                                    value={travelerFirstName} onChange={(e) => setTravelerFirstName(e.target.value)} className="h-[40px]" />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500">{t('lastName', { ns: 'dashboard' })}</label>
                                <Input id="lastName" type="text" invalid={false} placeholder={t('dialogs.placeholderLastName', { ns: 'dashboard' })}
                                    value={travelerLastName} onChange={(e) => setTravelerLastName(e.target.value)} className="h-[40px]" />
                            </div>
                            <div className="col-span-1 lg:col-span-2 flex flex-col gap-1">
                                <label className="text-sm text-gray-500">{t('email', { ns: 'dashboard' })}</label>
                                <Input id="email" type="email" invalid={false} placeholder={t('dialogs.placeholderTravelerEmail', { ns: 'dashboard' })}
                                    value={travelerEmail} onChange={(e) => setTravelerEmail(e.target.value)} className="h-[40px]" />
                            </div>
                        </div>
                    </SectionCard>

                    {/* Section 2: Trip details */}
                    <SectionCard title={t('tripData', { ns: 'dashboard' })} isCompleted={isTripComplete}>
                        <div className="flex flex-col gap-4">

                            {/* Dates */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500">
                                    <span className="text-danger">*</span> {t('selectDates', { ns: 'dashboard' })}
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-400">{t('start', { ns: 'dashboard' })}</span>
                                        <input
                                            type="date"
                                            value={tripStartDate}
                                            onChange={(e) => { setTripStartDate(e.target.value); handleDateChange(e.target.value, tripEndDate) }}
                                            className="h-[40px] border border-neutralBlack-300 rounded-[4px] px-3 text-sm bg-white focus:outline-none focus:border-primary"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-400">{t('end', { ns: 'dashboard' })}</span>
                                        <input
                                            type="date"
                                            value={tripEndDate}
                                            min={tripStartDate || undefined}
                                            onChange={(e) => { setTripEndDate(e.target.value); handleDateChange(tripStartDate, e.target.value) }}
                                            className="h-[40px] border border-neutralBlack-300 rounded-[4px] px-3 text-sm bg-white focus:outline-none focus:border-primary"
                                        />
                                    </div>
                                </div>
                                {dateError && <p className="text-sm text-danger">{dateError}</p>}
                            </div>

                            {/* Destinations */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500">
                                    <span className="text-danger">*</span> {t('countryCountries', { ns: 'dashboard' })}
                                </label>
                                <div className="flex flex-wrap gap-2 mb-1">
                                    {circuitCountries.map((code) => {
                                        const country = countries.find((c) => c.id === code)
                                        return (
                                            <div key={code} className="flex items-center gap-1 bg-neutralBlack-100 px-3 py-1 rounded-full text-sm">
                                                <img src={getFlagUrl(code)} alt={code} className="h-[15px] w-[22px] object-cover"
                                                    onError={(e) => { e.currentTarget.src = 'https://storage.googleapis.com/tripsapp-prod-cdn/backgroundCoverNoImage.jpg' }} />
                                                <span>{country ? ((country as any).title || (country as any).name) : code}</span>
                                                <button onClick={() => removeCountry(code)} className="text-gray-400 hover:text-red-500 ml-1">
                                                    <img src={crossIcon} alt="remove" className="size-3" />
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                                <AutocompleteSearch
                                    options={filteredCountryOptions}
                                    searchQuery={countrySearchQuery}
                                    onSearchQueryChange={setCountrySearchQuery}
                                    onSelectOption={handleSelectCountry}
                                    placeholder={t('tripForm.searchDestination', { ns: 'dashboard' })}
                                />
                            </div>

                            {/* Circuit name */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500">
                                    {t('circuitName', { ns: 'dashboard' })}
                                </label>
                                <Input id="circuitName" type="text" invalid={false}
                                    placeholder={t('itineraryTitlePlaceholder', { ns: 'dashboard' })}
                                    value={circuitName} onChange={(e) => setCircuitName(e.target.value)} className="h-[40px]" />
                            </div>
                            </div>

                            {/* PDF del itinerario */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500">{t('itineraryPdf', { ns: 'dashboard' })}</label>
                                <input
                                    ref={pdfInputRef}
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    className="hidden"
                                    onChange={handlePdfFileSelected}
                                />
                                {!itineraryPdfFileUrl && !isUploadingPdf && (
                                    <button
                                        type="button"
                                        onClick={() => pdfInputRef.current?.click()}
                                        className="h-[40px] flex items-center gap-2 border border-dashed border-neutralBlack-300 rounded-[4px] px-3 text-sm text-gray-400 hover:border-gray-400 transition-colors duration-200 ease-out cursor-pointer bg-white"
                                    >
                                        <span className="text-base">📎</span>
                                        {t('itineraryPdfAttach', { ns: 'dashboard' })}
                                    </button>
                                )}
                                {isUploadingPdf && (
                                    <div className="h-[40px] flex items-center gap-2 border border-neutralBlack-200 rounded-[4px] px-3 text-sm text-gray-400 bg-white">
                                        <IconSpinner className="size-4 shrink-0" />
                                        <span className="truncate">{t('uploadingPdf', { ns: 'dashboard' })}</span>
                                    </div>
                                )}
                                {itineraryPdfFileUrl && !isUploadingPdf && (
                                    <div className="h-[40px] flex items-center gap-2 border border-neutralBlack-200 rounded-[4px] px-3 text-sm bg-white">
                                        <span className="text-base shrink-0">📄</span>
                                        <span className="flex-1 truncate text-gray-700">{pdfFileName}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setItineraryPdfFileUrl('')
                                                setPdfFileName('')
                                                if (pdfInputRef.current) pdfInputRef.current.value = ''
                                            }}
                                            className="text-danger text-xs font-medium shrink-0 cursor-pointer"
                                        >
                                            {t('remove', { ns: 'dashboard' })}
                                        </button>
                                    </div>
                                )}
                            </div>

                        </div>
                    </SectionCard>

                    {/* Section 3: Order */}
                    <SectionCard title={t('orderData', { ns: 'dashboard' })} isCompleted={isOrderComplete}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500"><span className="text-danger">*</span> {t('writeTripReference', { ns: 'dashboard' })}</label>
                                <Input id="tripReference" type="text" invalid={false} placeholder="KJLIT65T"
                                    value={tripReference} onChange={(e) => setTripReference(e.target.value)} className="h-[40px]" />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500">{t('observations', { ns: 'dashboard' })}</label>
                                <Input id="observations" type="text" invalid={false}
                                    placeholder={t('addAdditionalInformation', { ns: 'dashboard' })}
                                    value={tripObservations} onChange={(e) => setTripObservations(e.target.value)} className="h-[40px]" />
                            </div>
                        </div>
                    </SectionCard>

                    {/* Submit */}
                    <div className="flex flex-col items-center gap-3">
                        {submitError && <p className="text-sm text-danger">{submitError}</p>}
                        <button
                            onClick={submit}
                            disabled={!isFormEnabled}
                            className="w-full max-w-sm py-3 px-8 rounded-xl bg-black text-white font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
                        >
                            {t('create', { ns: 'dashboard' })}
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading overlay */}
            {isLoading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <IconSpinner className="size-16 text-white" />
                </div>
            )}

            {/* Success modal */}
            {showSuccess && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowSuccess(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 flex flex-col items-center text-center gap-6">
                        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                            <CheckIcon />
                        </div>
                        <div className="flex flex-col gap-2">
                            <h2 className="text-2xl font-bold text-gray-900">{t('tripForm.successTitle', { ns: 'dashboard' })}</h2>
                            <p className="text-gray-500 text-sm leading-relaxed">{t('tripForm.successMessage', { ns: 'dashboard' })}</p>
                        </div>
                        <button
                            onClick={() => setShowSuccess(false)}
                            className="py-2 px-6 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors"
                        >
                            {t('done', { ns: 'dashboard' })}
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    )
}
